import { useMemo } from "react";
import { ColDef } from "ag-grid-community";
import locale from "@bonliva-crm/locale";
import { BookingKitTableColumns } from "@bonliva-crm/shared";
import { CustomCellRendererProps } from "ag-grid-react";
import { MarkHighlighter, WithTooltip } from "@common";
import moment from "moment";
import { AssignToKitCol, AssignToKitDialogData } from "./assign-to-kit-col";
import { BookingKitSearchItemFragment } from "@graphql";
import { compact } from "lodash";
import BookingKitTagStatusCol from "./booking-kit-tag-status-col";
import { useDialog } from "@hooks";
import { cellRendererLink } from "@common/renderers";
import { KitProfileLocation } from "@navigation";

export type KitSearchItemFragment = BookingKitSearchItemFragment;

export function useInitialColumnDefs(
  dialog: ReturnType<typeof useDialog<AssignToKitDialogData>>
): Array<ColDef<KitSearchItemFragment>> {
  const defs = useMemo(() => {
    const result: Array<ColDef> = [];

    result.push({
      field: BookingKitTableColumns.Week,
      headerName: locale.kitTableColumnType.Week,
      valueGetter: (params) => {
        return params.data?.week ? `${moment(params.data?.week).format("V.W YYYY")}` : "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.Assigned,
      headerName: locale.kitTableColumnType.Assigned,
      cellRenderer: (props: CustomCellRendererProps<KitSearchItemFragment>) => {
        return (
          <AssignToKitCol data={props.data as KitSearchItemFragment} dialog={dialog} />
        );
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.Consultant,
      headerName: locale.kitTableColumnType.Consultant,
      wrapText: true,
      sortable: false,
      cellRenderer: (props: CustomCellRendererProps<KitSearchItemFragment>) => {
        return (
          <MarkHighlighter
            highlightSearch={props.context?.highlightText}
            getMarkjsContext={() => props.eGridCell}
          >
            {props.data?.consultantName ?? ""}
          </MarkHighlighter>
        );
      }
    });

    result.push({
      field: BookingKitTableColumns.Customer,
      headerName: locale.kitTableColumnType.Customer,
      cellRenderer: (props: CustomCellRendererProps<KitSearchItemFragment>) => {
        return (
          <WithTooltip title={props.data?.customerBillingInstructions}>
            <div>
              {compact([
                props.data?.regionName,
                props.data?.hospitalName,
                props.data?.departmentName
              ]).map((x, i) => (
                <div key={i}>{x}</div>
              ))}
            </div>
          </WithTooltip>
        );
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.IsNationalProcurement,
      headerName: locale.kitTableColumnType.IsNationalProcurement,
      valueGetter: (params) => {
        return params.data?.isNationalProcurement ? "N" : "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.Aoe,
      headerName: locale.kitTableColumnType.Aoe,
      valueGetter: (params) => {
        return params.data?.areaOfExpertise ?? "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.CostCenter,
      headerName: locale.kitTableColumnType.CostCenter,
      valueGetter: (params) => {
        return params.data?.costCenterId ? `${params.data?.costCenterId}` : "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.Project,
      headerName: locale.kitTableColumnType.Project,
      valueGetter: (params) => {
        return params.data?.projectName ?? "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.Alt,
      headerName: locale.kitTableColumnType.Alt,
      valueGetter: (params) => {
        return params.data?.customerContractType ?? "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.KitStatus,
      headerName: locale.kitTableColumnType.KitStatus,
      valueGetter: (params) => {
        return locale.bookingKitStatusType[params.data?.lastStatusType]
          ? locale.bookingKitStatusType[params.data?.lastStatusType]
          : params.data?.lastStatusType ?? "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.LastEvent,
      headerName: locale.kitTableColumnType.LastEvent,
      cellRenderer: (props) => {
        return (
          <div>
            {compact([
              props.data?.lastStatusSetByUserName,
              props.data?.lastStatusSetOn
                ? moment(props.data?.lastStatusSetOn).format("YYYY-MM-DD HH:mm")
                : ""
            ]).map((x, i) => (
              <div key={i}>{x}</div>
            ))}
          </div>
        );
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.ManagementStatus,
      headerName: locale.kitTableColumnType.ManagementStatus,
      cellRenderer: (props) => (
        <BookingKitTagStatusCol
          tags={props.data?.bookingSupportTagStatus ?? []}
          comment={props.data?.bookingSupportTagComment}
        />
      ),
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.Type,
      headerName: locale.kitTableColumnType.Type,
      valueGetter: (params) => {
        return params.data?.type ? locale.bookingKitType[params.data.type] : "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.ConsultantPayoutType,
      headerName: locale.kitTableColumnType.ConsultantPayoutType,
      valueGetter: (params) => {
        return params.data?.consultantPayoutType ?? "";
      },
      wrapText: true,
      sortable: false
    });

    result.push({
      field: BookingKitTableColumns.TimereportApprovedOn,
      headerName: locale.kitTableColumnType.TimereportApprovedOn,
      valueGetter: (params) => {
        return params.data?.timereportApprovedOn
          ? moment(params.data?.timereportApprovedOn).format("YYYY-MM-DD HH:mm")
          : "";
      },
      wrapText: true,
      sortable: false
    });

    // todo could be added later
    // result.push({
    //   field: BookingKitTableColumns.Tags,
    //   headerName: locale.kitTableColumnType.Tags,
    //   cellRenderer: (props) => <TagsRenderer tags={props.data?.tags} />,
    //   wrapText: true,
    //   cellStyle: { padding: 0 }
    // });

    const resultWithLinks = result.map((colDef) => {
      return {
        ...colDef,
        cellRenderer: cellRendererLink(colDef.cellRenderer, (params) => {
          const id = (params.data as BookingKitSearchItemFragment)?._id;
          const location = new KitProfileLocation(id);

          return {
            to: location.toURI(),
            onClick: (e) => e.preventDefault()
          };
        })
      };
    }) as Array<ColDef<KitSearchItemFragment>>;

    return resultWithLinks;
  }, []);

  return defs;
}
